import type { ComponentType, ReactElement, ReactNode, SyntheticEvent } from 'react';
import { memo, useTransition } from 'react';
import type { StripeTitle, WrapperFC } from '@george-labs.com/design-system';
import { Button, StripeHeader } from '@george-labs.com/design-system';
import type { COLOR } from '@george-labs.com/design-system/types/utilsTS/color';

import { t } from '@luna/i18n';
import { useTemporaryMessageHeaderHeight } from '@shared/temporary-messages';
import { getFeatureConfig } from '@luna/feature-config';

import { FocusPageStripeCenter } from './FocusPageStripeCenter';
import { getStickyOffset } from './FocusPageStripeService';

interface CloseButtonProps {
    showClose?: boolean;
    closeDataCy?: string;
    closeHref?: string;
    closeDisabled?: boolean;
    closeComponent?: ComponentType;
    onCloseClick?: (event: SyntheticEvent) => void;
}

export interface Props extends CloseButtonProps {
    titleDataCy?: string;
    title: string | ReactElement<typeof StripeTitle>;
    titleOverline?: string;
    left?: ReactNode;
    color?: COLOR;
    stripeClassName?: string;
    stripeDataCy?: string;
}

export const FocusPageStripe: WrapperFC = memo<Props>(function FocusPageStripe({
    titleDataCy = 'focus-page-title',
    title,
    titleOverline,
    color,
    left,
    closeDataCy = 'focus-page-close-btn',
    closeHref,
    closeDisabled,
    closeComponent,
    stripeClassName,
    stripeDataCy,
    onCloseClick,
    showClose = true,
}) {
    const temporaryMessageHeaderHeight = useTemporaryMessageHeaderHeight();
    const stripeColor = getFeatureConfig((config) => config.base.defaultStripeColor);
    const [isPending, startTransition] = useTransition();

    const onClick = (event: SyntheticEvent) => {
        startTransition(() => onCloseClick?.(event));
    };

    return (
        <StripeHeader
            left={left}
            center={
                <FocusPageStripeCenter data-cy={titleDataCy} overline={titleOverline}>
                    {title}
                </FocusPageStripeCenter>
            }
            right={
                showClose && (
                    <Button
                        isLoading={isPending}
                        data-cy={closeDataCy}
                        icon={Button.ICON.ERROR}
                        href={closeHref}
                        onClick={onClick}
                        variant={Button.VARIANT.TERTIARY}
                        size={Button.SIZE.SMALL}
                        disabled={closeDisabled}
                        component={closeComponent}
                        // @ts-expect-error - invalid typing // TODO: GDS solution to prop typing button
                        to={closeHref}
                    >
                        {t('app.close')}
                    </Button>
                )
            }
            data-cy={stripeDataCy}
            className={stripeClassName}
            color={color ?? stripeColor}
            sticky
            stickyOffset={getStickyOffset(temporaryMessageHeaderHeight)}
        />
    );
});

FocusPageStripe.wrapsComponent = StripeHeader;
